import { useState, useEffect } from "react";
import useInstallPrompt from "@/hooks/useInstallPrompt";
import { DownloadSimple, X, DeviceMobile, Share, Plus } from "@phosphor-icons/react";

const DISMISS_KEY = "dyd_install_dismissed_at";
const DISMISS_TTL_DAYS = 14;

/**
 * Floating install-app banner. Shown on the first visit (and every 14 days
 * after a dismissal) when:
 *  - the user is on a mobile device,
 *  - the app is not already running standalone,
 *  - either Chromium fired `beforeinstallprompt` OR the user is on iOS Safari
 *    (where we render the manual "Compartir → Añadir a pantalla de inicio"
 *    instructions).
 */
export default function InstallAppBanner() {
  const { canInstall, isStandalone, isInstalled, isIOS, isAndroid, promptInstall } = useInstallPrompt();
  const [hidden, setHidden] = useState(true);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone || isInstalled) return;
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    const fresh = !ts || (Date.now() - ts) > DISMISS_TTL_DAYS * 86400 * 1000;
    if (!fresh) return;
    // Show on mobile only — desktop install is less impactful.
    const isMobile = isIOS || isAndroid || window.innerWidth < 768;
    if (!isMobile) return;
    // Wait a bit so the banner doesn't fight with the first paint.
    const t = setTimeout(() => setHidden(false), 4500);
    return () => clearTimeout(t);
  }, [isStandalone, isInstalled, isIOS, isAndroid]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setHidden(true);
    setShowIosHelp(false);
  };

  const handleInstall = async () => {
    if (canInstall) {
      const outcome = await promptInstall();
      if (outcome) dismiss();
    } else if (isIOS) {
      setShowIosHelp(true);
    }
  };

  if (hidden || isStandalone || isInstalled) return null;

  return (
    <div
      role="dialog"
      aria-label="Instalar la app"
      data-testid="install-app-banner"
      className="fixed left-3 right-3 z-[120] md:max-w-md md:left-auto md:right-4 md:bottom-4 animate-fade-in"
      style={{ bottom: "calc(76px + env(safe-area-inset-bottom))" }}
    >
      <div className="card-tactical bg-black/95 backdrop-blur border border-[#CCFF00]/40 p-3 sm:p-4 shadow-2xl shadow-[#CCFF00]/10 rounded-md">
        <button
          onClick={dismiss}
          aria-label="Cerrar"
          data-testid="install-app-dismiss"
          className="absolute top-1 right-1 text-gray-400 hover:text-white p-1.5"
        >
          <X size={14} />
        </button>
        {!showIosHelp ? (
          <div className="flex items-center gap-3 pr-6">
            <div className="shrink-0 w-10 h-10 rounded-md bg-[#CCFF00]/15 border border-[#CCFF00]/30 flex items-center justify-center">
              <DeviceMobile size={22} weight="fill" className="text-[#CCFF00]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#CCFF00]">Instala la app</p>
              <p className="text-sm text-white font-semibold leading-tight">Disfruta y Diviértete · sin abrir el navegador</p>
              <p className="text-[11px] text-gray-400 mt-0.5">Notificaciones, marcadores en vivo y acceso offline.</p>
            </div>
            <button
              onClick={handleInstall}
              data-testid="install-app-cta"
              className="shrink-0 bg-[#CCFF00] hover:bg-[#B3E600] text-black font-bold uppercase tracking-wide text-[11px] px-3 py-2 rounded inline-flex items-center gap-1"
            >
              <DownloadSimple size={14} weight="bold" /> Instalar
            </button>
          </div>
        ) : (
          <div className="pr-6" data-testid="install-app-ios-help">
            <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#CCFF00] mb-1">Instala en iPhone / iPad</p>
            <ol className="text-[12px] text-gray-200 space-y-1.5 mt-1.5">
              <li className="flex items-start gap-2"><span className="text-[#CCFF00] font-bold">1.</span> Toca <Share size={14} className="inline align-text-bottom" /> <span className="font-semibold">Compartir</span> en la barra de Safari.</li>
              <li className="flex items-start gap-2"><span className="text-[#CCFF00] font-bold">2.</span> Desplaza y toca <Plus size={14} className="inline align-text-bottom" /> <span className="font-semibold">Añadir a pantalla de inicio</span>.</li>
              <li className="flex items-start gap-2"><span className="text-[#CCFF00] font-bold">3.</span> Confirma con <span className="font-semibold">Añadir</span>. El icono &ldquo;DyD Pádel&rdquo; aparecerá en tu home.</li>
            </ol>
            <button
              onClick={dismiss}
              data-testid="install-app-ios-ok"
              className="mt-3 bg-[#CCFF00] hover:bg-[#B3E600] text-black font-bold uppercase tracking-wide text-[11px] px-3 py-2 rounded w-full"
            >
              Entendido
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
